import React, { useEffect, useState } from 'react';
import { X, Save, Trash2, Copy, Palette, FileText, AlignLeft, AlignCenter, AlignRight, Image as ImageIcon } from 'lucide-react';
import FieldInput from './FieldInput';
import MediaPicker from '../../../components/media/MediaPicker';

/**
 * BlockEditPanel — opens as a side panel when the admin clicks ✏️ on a custom
 * block. Tabs: "محتوى" (data fields from block schema) + "ستايل" (background,
 * padding, container, alignment). Same UX shape as WebsiteBuilder's section
 * card style panel — kept consistent so admins don't context-switch.
 *
 * Props:
 *   block       — the custom block being edited ({ id, type, data, style, ... })
 *   blockDef    — its catalog entry (label, schema, ...)
 *   onChange    — (patch) => void  — partial update (data/style/enabled)
 *   onDelete    — () => void
 *   onDuplicate — () => void
 *   onClose     — () => void
 */
export default function BlockEditPanel({ block, blockDef, onChange, onDelete, onDuplicate, onClose }) {
  const [tab, setTab] = useState('content');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [bgPickerOpen, setBgPickerOpen] = useState(false);

  // ESC closes the panel — keyboard parity with the picker modal.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (confirmDelete) setConfirmDelete(false);
        else if (bgPickerOpen) setBgPickerOpen(false);
        else onClose?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirmDelete, bgPickerOpen, onClose]);

  if (!block || !blockDef) return null;

  const data = block.data || {};
  const style = block.style || {};
  const bg = style.background || {};
  const pad = style.padding || {};

  const setField = (key, value) => {
    const def = (blockDef.schema?.fields || []).find((f) => f.key === key);
    const next = { ...data };
    // If the new value equals manifest default, drop it so we don't persist
    // unnecessary noise — matches the convention used by core sections.
    if (def && JSON.stringify(value) === JSON.stringify(def.default)) {
      delete next[key];
    } else {
      next[key] = value;
    }
    onChange({ data: next });
  };

  const setStyleField = (path, value) => {
    const next = { ...style };
    if (path === 'background.type') {
      if (value === 'none') {
        delete next.background;
      } else {
        next.background = { ...(next.background || {}), type: value };
      }
    } else if (path === 'background.color') {
      next.background = { ...(next.background || { type: 'color' }), color: value };
    } else if (path === 'background.imageUrl') {
      next.background = { ...(next.background || { type: 'image' }), imageUrl: value };
    } else if (path === 'padding.top') {
      next.padding = { ...(next.padding || {}), top: value };
    } else if (path === 'padding.bottom') {
      next.padding = { ...(next.padding || {}), bottom: value };
    } else if (path === 'containerWidth') {
      if (value) next.containerWidth = value;
      else delete next.containerWidth;
    } else if (path === 'textAlign') {
      if (value) next.textAlign = value;
      else delete next.textAlign;
    }
    onChange({ style: next });
  };

  return (
    <div className="fixed inset-y-0 left-0 z-[90] w-full sm:w-[420px] bg-white border-r border-slate-200 shadow-2xl flex flex-col" dir="rtl">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-slate-900 truncate">{blockDef.label?.ar || block.type}</h3>
          <p className="text-[11px] text-slate-400 font-mono truncate" dir="ltr">{block.type}</p>
        </div>
        <button
          type="button"
          onClick={onDuplicate}
          title="نسخ هذا القسم"
          className="p-2 text-slate-500 hover:text-blue-600 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <Copy size={15} />
        </button>
        <button
          type="button"
          onClick={() => setConfirmDelete(true)}
          title="حذف"
          className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        >
          <Trash2 size={15} />
        </button>
        <button
          type="button"
          onClick={onClose}
          title="إغلاق"
          className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <X size={16} />
        </button>
      </div>

      {/* Delete confirmation banner */}
      {confirmDelete && (
        <div className="px-4 py-3 bg-red-50 border-b border-red-200">
          <p className="text-sm font-bold text-red-800 mb-1">حذف هذا القسم؟</p>
          <p className="text-[12px] text-red-700 mb-3">سيتم حذف كل المحتوى داخله نهائياً عند النشر.</p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => { setConfirmDelete(false); onDelete?.(); }}
              className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-[12px] font-bold hover:bg-red-700"
            >نعم، احذف</button>
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              className="px-3 py-1.5 bg-white border border-slate-200 text-slate-600 rounded-lg text-[12px] font-semibold hover:bg-slate-50"
            >إلغاء</button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-100 flex">
        <button
          type="button"
          onClick={() => setTab('content')}
          className={`flex-1 px-4 py-2.5 text-[12px] font-semibold transition-colors inline-flex items-center justify-center gap-1.5 ${tab === 'content' ? 'border-b-2 border-blue-600 text-blue-700 bg-blue-50/40' : 'text-slate-500 hover:text-slate-700'}`}
        >
          <FileText size={14} /> المحتوى
        </button>
        <button
          type="button"
          onClick={() => setTab('style')}
          className={`flex-1 px-4 py-2.5 text-[12px] font-semibold transition-colors inline-flex items-center justify-center gap-1.5 ${tab === 'style' ? 'border-b-2 border-blue-600 text-blue-700 bg-blue-50/40' : 'text-slate-500 hover:text-slate-700'}`}
        >
          <Palette size={14} /> الستايل
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4">
        {tab === 'content' && (
          <div className="space-y-4">
            {(blockDef.schema?.fields || []).map((field) => (
              <div key={field.key}>
                {field.type !== 'toggle' && (
                  <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">
                    {field.label}
                  </label>
                )}
                <FieldInput
                  field={field}
                  value={data[field.key] !== undefined ? data[field.key] : field.default}
                  onChange={(v) => setField(field.key, v)}
                />
              </div>
            ))}
          </div>
        )}

        {tab === 'style' && (
          <div className="space-y-5">
            {/* Background */}
            <div>
              <label className="block text-[12px] font-semibold text-slate-700 mb-2">الخلفية</label>
              <div className="flex items-center gap-2 mb-2">
                {[
                  { v: 'none', label: 'بدون' },
                  { v: 'color', label: 'لون' },
                  { v: 'image', label: 'صورة' },
                ].map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setStyleField('background.type', o.v)}
                    className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-colors ${(bg.type || 'none') === o.v ? 'bg-blue-600 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                  >{o.label}</button>
                ))}
              </div>
              {bg.type === 'color' && (
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={bg.color || '#ffffff'}
                    onChange={(e) => setStyleField('background.color', e.target.value)}
                    className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer p-0"
                  />
                  <input
                    type="text"
                    value={bg.color || ''}
                    onChange={(e) => setStyleField('background.color', e.target.value)}
                    placeholder="#ffffff"
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-[13px] font-mono"
                    dir="ltr"
                  />
                </div>
              )}
              {bg.type === 'image' && (
                <div className="space-y-2">
                  {bg.imageUrl ? (
                    <div className="flex items-center gap-2 p-2 rounded-lg border border-slate-200 bg-slate-50">
                      <img src={bg.imageUrl} alt="" className="w-14 h-14 rounded object-cover shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] text-slate-500 font-mono truncate" dir="ltr">{bg.imageUrl}</div>
                        <div className="flex items-center gap-1 mt-1">
                          <button type="button" onClick={() => setBgPickerOpen(true)} className="text-[11px] text-blue-600 hover:underline">تغيير</button>
                          <span className="text-slate-300">·</span>
                          <button type="button" onClick={() => setStyleField('background.imageUrl', '')} className="text-[11px] text-blue-600 hover:underline">إزالة</button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setBgPickerOpen(true)}
                      className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border border-dashed border-slate-300 bg-slate-50 text-sm text-slate-500 hover:border-blue-300 hover:bg-blue-50/40 transition-colors"
                    >
                      <ImageIcon className="w-4 h-4" />
                      اختر من المكتبة
                    </button>
                  )}
                  <input
                    type="text"
                    value={bg.imageUrl || ''}
                    onChange={(e) => setStyleField('background.imageUrl', e.target.value)}
                    placeholder="أو ألصق رابط مباشر..."
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] font-mono"
                    dir="ltr"
                  />
                </div>
              )}
            </div>

            {/* Padding — slider values default to 0 visually so the readout
                matches the slider position (no "–" mismatch). */}
            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1 text-[11px] text-slate-600">
                <span>هامش علوي ({pad.top ?? 0}px{pad.top == null ? ' — افتراضي' : ''})</span>
                <input
                  type="range" min={0} max={200} step={4}
                  value={pad.top ?? 0}
                  onChange={(e) => setStyleField('padding.top', Number(e.target.value))}
                  className="h-1.5 accent-blue-600"
                />
              </label>
              <label className="flex flex-col gap-1 text-[11px] text-slate-600">
                <span>هامش سفلي ({pad.bottom ?? 0}px{pad.bottom == null ? ' — افتراضي' : ''})</span>
                <input
                  type="range" min={0} max={200} step={4}
                  value={pad.bottom ?? 0}
                  onChange={(e) => setStyleField('padding.bottom', Number(e.target.value))}
                  className="h-1.5 accent-blue-600"
                />
              </label>
            </div>

            {/* Container width */}
            <div>
              <label className="block text-[12px] font-semibold text-slate-700 mb-2">عرض القسم</label>
              <div className="grid grid-cols-4 gap-1">
                {[
                  { v: '', label: 'افتراضي' },
                  { v: 'narrow', label: 'ضيق' },
                  { v: 'normal', label: 'عادي' },
                  { v: 'wide', label: 'واسع' },
                ].map((o) => (
                  <button
                    key={o.v || 'default'}
                    type="button"
                    onClick={() => setStyleField('containerWidth', o.v)}
                    className={`px-2 py-1.5 rounded-lg text-[11px] font-semibold transition-colors ${(style.containerWidth || '') === o.v ? 'bg-blue-600 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                  >{o.label}</button>
                ))}
              </div>
            </div>

            {/* Text alignment */}
            <div>
              <label className="block text-[12px] font-semibold text-slate-700 mb-2">محاذاة النص</label>
              <div className="grid grid-cols-4 gap-1">
                {[
                  { v: '', icon: null },
                  { v: 'start', icon: AlignRight },
                  { v: 'center', icon: AlignCenter },
                  { v: 'end', icon: AlignLeft },
                ].map((o) => {
                  const Icon = o.icon;
                  return (
                    <button
                      key={o.v || 'default'}
                      type="button"
                      onClick={() => setStyleField('textAlign', o.v)}
                      className={`px-2 py-2 rounded-lg text-[11px] font-semibold inline-flex items-center justify-center gap-0.5 transition-colors ${(style.textAlign || '') === o.v ? 'bg-blue-600 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                    >
                      {Icon ? <Icon className="w-3.5 h-3.5" /> : <span>—</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer hint */}
      <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50/60">
        <p className="text-[11px] text-slate-500 text-center">
          التغييرات تُحفظ تلقائياً مع باقي تعديلات الصفحة عند الضغط على "حفظ مسودة"
        </p>
      </div>

      {/* MediaPicker for background image */}
      <MediaPicker
        open={bgPickerOpen}
        onClose={() => setBgPickerOpen(false)}
        onPick={(asset) => { setStyleField('background.imageUrl', asset?.publicUrl || ''); setBgPickerOpen(false); }}
        folder="backgrounds"
      />
    </div>
  );
}
